import React, { FormEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Alert, Button, Col, Container, Row, Spinner } from "reactstrap";
import { Fancybox } from "@fancyapps/ui";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import { get } from "src/helpers/api_helper";
import { getAuthUser } from "src/helpers/auth";
import {
  acceptTicket,
  deleteTicketCannedResponse,
  deleteTicket,
  deleteTicketRelation,
  deleteTicketWatch,
  getTicket,
  getTicketActivity,
  getTicketCannedResponses,
  getTicketMessages,
  getTicketRelations,
  getUsersAll,
  postTicketCannedResponse,
  postTicketAssign,
  postTicketMessage,
  postTicketMessagesRead,
  postTicketRating,
  postTicketRelation,
  postTicketWatch,
  postUseTicketCannedResponse,
  updateTicketCannedResponse,
  updateTicket,
} from "src/helpers/backend_helper";
import { normalizeUserOptions } from "src/helpers/normalizeUserOptions";
import { ticketBus, TicketMessageRealtimePayload, TicketRealtimePayload } from "src/helpers/ticketEventBus";
import usePermissions from "src/hooks/usePermissions";
import { MyEcho } from "src/providers/BroadcastProvider";
import TicketConversationPanel from "./ticket-details/ConversationPanel";
import {
  TicketAddRelationModal,
  TicketCannedResponseModal,
  TicketCannedResponseDeleteModal,
  TicketDeleteModal,
  TicketRatingModal,
  TicketReassignModal,
} from "./ticket-details/Modals";
import TicketDetailsSidebar from "./ticket-details/Sidebar";
import {
  ActivityItem,
  AssignableUser,
  CannedResponse,
  MessageGroup,
  Pagination,
  TicketAttachment,
  TicketData,
  TicketMessage,
  TicketRelation,
  TicketStatus,
  TicketTab,
  TicketUser,
} from "./ticket-details/types";
import {
  STATUS_OPTIONS,
  getConversationDateKey,
  getConversationDateLabel,
} from "./ticket-details/utils";

const getApiErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.message
  || error?.message
  || fallback;

type CannedResponseScope = "all" | "category";

const TicketDetailsPage = () => {
  const navigate = useNavigate();
  const { uuid } = useParams<{ uuid: string }>();
  const ticketRouteKey = uuid?.trim() || "";

  const { hasPermission } = usePermissions();
  const canView = hasPermission("view-tickets");
  const canViewInternalNotes = hasPermission("view-internal-notes");
  const canUseCannedResponses = hasPermission("reply-tickets");
  const authUser = useMemo(() => getAuthUser(), []);
  const authRoleNames = useMemo(
    () => authUser?.roles?.map((role) => String(role.name)) ?? [],
    [authUser?.roles]
  );

  const [loading, setLoading] = useState<boolean>(false);
  const [ticketLoadError, setTicketLoadError] = useState<string>("");
  const [ticketNotFound, setTicketNotFound] = useState<boolean>(false);
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesLoadError, setMessagesLoadError] = useState<string>("");
  const [messagesPagination, setMessagesPagination] = useState<Pagination>({
    current_page: 1,
    total_page: 1,
  });
  const [messageBody, setMessageBody] = useState<string>("");
  const [messageFiles, setMessageFiles] = useState<File[]>([]);
  const [messageFileInputKey, setMessageFileInputKey] = useState<number>(0);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState<Record<number, string>>({});
  const [previewFailedIds, setPreviewFailedIds] = useState<Record<number, boolean>>({});
  const [sendingMessage, setSendingMessage] = useState<boolean>(false);
  const [savingTicket, setSavingTicket] = useState<boolean>(false);
  const [deletingTicket, setDeletingTicket] = useState<boolean>(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<string>("");
  const attachmentPreviewUrlsRef = useRef<Record<number, string>>({});
  const previewFailedIdsRef = useRef<Record<number, boolean>>({});
  const previewRequestMapRef = useRef<Record<number, Promise<string | null>>>({});
  const conversationWrapRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const shouldAutoScrollRef = useRef<boolean>(false);
  const typingHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingWhisperCooldownRef = useRef<number>(0);
  const presenceChannelRef = useRef<any>(null);

  const [editSubject, setEditSubject] = useState<string>("");
  const [editDescription, setEditDescription] = useState<string>("");
  const [editStatus, setEditStatus] = useState<TicketStatus>("open");
  const [editPriority, setEditPriority] = useState<number>(2);
  const [isInternalNote, setIsInternalNote] = useState<boolean>(false);
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<TicketTab>("conversation");
  const [showCannedResponses, setShowCannedResponses] = useState<boolean>(false);
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [showCannedResponseModal, setShowCannedResponseModal] = useState<boolean>(false);
  const [editingCannedResponse, setEditingCannedResponse] = useState<CannedResponse | null>(null);
  const [deletingCannedResponse, setDeletingCannedResponse] = useState<CannedResponse | null>(null);
  const [removingCannedResponse, setRemovingCannedResponse] = useState<boolean>(false);
  const [cannedResponseTitle, setCannedResponseTitle] = useState<string>("");
  const [cannedResponseBody, setCannedResponseBody] = useState<string>("");
  const [cannedResponseScope, setCannedResponseScope] = useState<CannedResponseScope>("all");
  const [cannedResponseShared, setCannedResponseShared] = useState<boolean>(false);
  const [savingCannedResponse, setSavingCannedResponse] = useState<boolean>(false);
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [ratingHover, setRatingHover] = useState<number>(0);
  const [ratingComment, setRatingComment] = useState<string>("");
  const [submittingRating, setSubmittingRating] = useState<boolean>(false);
  const [showRatingModal, setShowRatingModal] = useState<boolean>(false);
  const [onlineUsers, setOnlineUsers] = useState<TicketUser[]>([]);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [showReassignModal, setShowReassignModal] = useState<boolean>(false);
  const [reassignUserId, setReassignUserId] = useState<number | "">("");
  const [reassigningTicket, setReassigningTicket] = useState<boolean>(false);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [isWatching, setIsWatching] = useState<boolean>(false);
  const [watchLoading, setWatchLoading] = useState<boolean>(false);
  const [relations, setRelations] = useState<TicketRelation[]>([]);
  const [showAddRelationModal, setShowAddRelationModal] = useState<boolean>(false);
  const [relationTicketId, setRelationTicketId] = useState<string>("");
  const [relationType, setRelationType] = useState<string>("related");
  const [addingRelation, setAddingRelation] = useState<boolean>(false);

  const hasUpdatePermission = Boolean(ticket?.permissions?.can_update);
  const canDelete = Boolean(ticket?.permissions?.can_delete);
  const canAccept = Boolean(ticket?.permissions?.can_accept);
  const canReply = Boolean(ticket?.permissions?.can_reply);
  const canAssign = Boolean(ticket?.permissions?.can_assign);
  const canCreateCannedResponses = canUseCannedResponses;
  const canManageSharedCannedResponses = useMemo(
    () => authRoleNames.some((roleName) => ["admin", "superadmin", "leadmanager"].includes(roleName)),
    [authRoleNames]
  );
  const currentUserId = useMemo(() => Number(authUser?.user_id || 0), [authUser?.user_id]);
  const currentUserName = useMemo(() => {
    return String(authUser?.username || authUser?.email || `User ${currentUserId}`);
  }, [authUser?.email, authUser?.username, currentUserId]);
  const isCurrentUserCreator = useMemo(() => {
    const creatorId = Number(ticket?.created_by_user_id || ticket?.creator?.id || 0);
    return creatorId > 0 && creatorId === currentUserId;
  }, [currentUserId, ticket?.created_by_user_id, ticket?.creator?.id]);
  const isCurrentUserAssignee = useMemo(() => {
    return Boolean(ticket?.assignee?.id) && Number(ticket?.assignee?.id || 0) === currentUserId;
  }, [currentUserId, ticket?.assignee?.id]);
  const canUpdateAllTicketFields = hasUpdatePermission && isCurrentUserCreator;
  const canUpdateOnlyStatus = hasUpdatePermission && !isCurrentUserCreator && isCurrentUserAssignee;
  const canUpdateTicket = canUpdateAllTicketFields || canUpdateOnlyStatus;
  const canManageRelations = canUpdateAllTicketFields;
  const isConversationLocked = useMemo(() => {
    return ticket?.status === "resolved" || ticket?.status === "closed";
  }, [ticket?.status]);
  const canSendMessage = canReply && !isConversationLocked;
  const isMacKeyboardPlatform = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return /(Mac|iPhone|iPod|iPad)/i.test(window.navigator.platform);
  }, []);
  const sendMessageShortcutLabel = isMacKeyboardPlatform ? "Cmd+Enter" : "Ctrl+Enter";
  const cannedResponseCategoryLabel = useMemo(() => {
    const categoryName = ticket?.category?.name?.trim();

    if (categoryName) {
      return `Category: ${categoryName}`;
    }

    return "this ticket scope";
  }, [ticket?.category?.name]);
  const canRateTicket = useMemo(() => {
    return isCurrentUserCreator && (ticket?.status === "resolved" || ticket?.status === "closed");
  }, [isCurrentUserCreator, ticket?.status]);
  const canViewFeedback = useMemo(() => {
    return canRateTicket || (isCurrentUserAssignee && Boolean(ticket?.satisfaction_rating));
  }, [canRateTicket, isCurrentUserAssignee, ticket?.satisfaction_rating]);
  const messageComposerDisabled = sendingMessage || (!messageBody.trim() && messageFiles.length === 0);
  const ratingPreviewValue = ratingHover || ratingValue;
  const availableStatusOptions = useMemo(() => {
    if (isCurrentUserCreator) {
      return STATUS_OPTIONS;
    }
    if (isCurrentUserAssignee && ticket?.status === "in_progress") {
      return STATUS_OPTIONS.filter((option) => option.value === "in_progress" || option.value === "resolved");
    }
    return STATUS_OPTIONS.filter((option) => option.value !== "closed");
  }, [isCurrentUserCreator, isCurrentUserAssignee, ticket?.status]);
  const deleteExpectedPhrase = ticket ? `delete ${ticket.id}` : "";
  const canConfirmDelete = Boolean(ticket)
    && deleteConfirmation.trim() === deleteExpectedPhrase
    && !deletingTicket;

  const canEditCannedResponse = useCallback((response: CannedResponse): boolean => {
    if (!canUseCannedResponses) {
      return false;
    }

    if (response.is_shared) {
      return canManageSharedCannedResponses;
    }

    return (response.created_by_user_id ?? 0) === currentUserId || canManageSharedCannedResponses;
  }, [canManageSharedCannedResponses, canUseCannedResponses, currentUserId]);

  const canDeleteCannedResponse = useCallback((response: CannedResponse): boolean => {
    if (!canUseCannedResponses) {
      return false;
    }

    if (response.is_shared) {
      return canManageSharedCannedResponses;
    }

    return (response.created_by_user_id ?? 0) === currentUserId || canManageSharedCannedResponses;
  }, [canManageSharedCannedResponses, canUseCannedResponses, currentUserId]);

  useEffect(() => {
    attachmentPreviewUrlsRef.current = attachmentPreviewUrls;
  }, [attachmentPreviewUrls]);

  useEffect(() => {
    previewFailedIdsRef.current = previewFailedIds;
  }, [previewFailedIds]);

  useEffect(() => {
    return () => {
      Object.values(attachmentPreviewUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const cacheAttachmentPreview = useCallback((attachmentId: number, objectUrl: string) => {
    setAttachmentPreviewUrls((prev) => {
      if (prev[attachmentId]) {
        URL.revokeObjectURL(objectUrl);
        return prev;
      }

      const next = { ...prev, [attachmentId]: objectUrl };
      attachmentPreviewUrlsRef.current = next;
      return next;
    });
  }, []);

  const loadAttachmentPreviewUrl = useCallback(
    async (attachment: TicketAttachment): Promise<string | null> => {
      if (!attachment.is_image || !attachment.download_url) {
        return null;
      }

      const cachedPreview = attachmentPreviewUrlsRef.current[attachment.id];
      if (cachedPreview) {
        return cachedPreview;
      }
      if (previewFailedIdsRef.current[attachment.id]) {
        return null;
      }

      const inFlight = previewRequestMapRef.current[attachment.id];
      if (inFlight) {
        return inFlight;
      }

      const request = get<Blob>(attachment.download_url, { responseType: "blob" })
        .then((blob) => {
          const objectUrl = URL.createObjectURL(blob);
          cacheAttachmentPreview(attachment.id, objectUrl);
          return objectUrl;
        })
        .catch(() => {
          setPreviewFailedIds((prev) => ({ ...prev, [attachment.id]: true }));
          return null;
        })
        .finally(() => {
          delete previewRequestMapRef.current[attachment.id];
        });

      previewRequestMapRef.current[attachment.id] = request;
      return request;
    },
    [cacheAttachmentPreview]
  );

  const ensureAttachmentPreviewLoaded = useCallback((attachment: TicketAttachment) => {
    void loadAttachmentPreviewUrl(attachment);
  }, [loadAttachmentPreviewUrl]);

  const handleDownloadAttachment = useCallback(async (attachment: TicketAttachment) => {
    if (!attachment.download_url) {
      return;
    }

    try {
      const blob = await get<Blob>(attachment.download_url, { responseType: "blob" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = attachment.original_name || `attachment-${attachment.id}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
    } catch {
      toast.error("Failed to download attachment.");
    }
  }, []);

  const openImagePreview = useCallback(
    async (attachment: TicketAttachment) => {
      const images = messages
        .flatMap((m) => m.attachments ?? [])
        .filter((a) => a.is_image && !a.is_broken);

      const gallery = images.length > 0 ? images : [attachment];
      const startIndex = Math.max(0, gallery.findIndex((a) => a.id === attachment.id));

      const urls = await Promise.all(gallery.map((a) => loadAttachmentPreviewUrl(a)));

      const slides = gallery
        .map((a, i) => ({ a, url: urls[i] }))
        .filter((s): s is { a: TicketAttachment; url: string } => Boolean(s.url))
        .map(({ a, url }) => ({
          src: url,
          type: "image" as const,
          caption: a.original_name,
        }));

      if (slides.length === 0) {
        toast.error("Failed to load image preview.");
        return;
      }

      const adjustedStart = Math.min(startIndex, slides.length - 1);
      Fancybox.show(slides, { startIndex: Math.max(0, adjustedStart) });
    },
    [loadAttachmentPreviewUrl, messages]
  );

  const hydrateTicketForm = useCallback((ticketData: TicketData) => {
    setEditSubject(ticketData.subject);
    setEditDescription(ticketData.description);
    setEditStatus(ticketData.status);
    setEditPriority(ticketData.priority);
  }, []);

  const loadTicket = useCallback(async () => {
    if (!ticketRouteKey) return;
    setLoading(true);
    setTicketLoadError("");
    setTicketNotFound(false);
    try {
      const response: any = await getTicket(ticketRouteKey);
      const ticketData: TicketData | null = response?.data ?? null;
      if (!ticketData) {
        throw new Error("Ticket not found");
      }

      setTicketLoadError("");
      setTicketNotFound(false);
      setTicket(ticketData);
      hydrateTicketForm(ticketData);
      if (typeof ticketData.is_watching === "boolean") {
        setIsWatching(ticketData.is_watching);
      }
    } catch (error: any) {
      const isNotFoundError = error?.response?.status === 404;
      setTicketNotFound(isNotFoundError);

      if (isNotFoundError) {
        setTicket(null);
        setTicketLoadError("");
        return;
      }

      setTicketLoadError(getApiErrorMessage(error, "Failed to load ticket."));
    } finally {
      setLoading(false);
    }
  }, [hydrateTicketForm, ticketRouteKey]);

  const loadMessages = useCallback(
    async (page: number = 1, prepend: boolean = false) => {
      if (!ticketRouteKey) return;
      setMessagesLoading(true);
      try {
        const response: any = await getTicketMessages(ticketRouteKey, "", {
          page,
          per_page: 50,
        });
        const messageData: TicketMessage[] = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];

        if (!prepend) {
          shouldAutoScrollRef.current = true;
        }
        setMessagesLoadError("");
        setMessages((prev) => (prepend ? [...messageData, ...prev] : messageData));
        setMessagesPagination({
          current_page: response?.pagination?.current_page || page,
          total_page: response?.pagination?.total_page || 1,
        });
      } catch (error: any) {
        setMessagesLoadError(getApiErrorMessage(error, "Failed to load conversation."));
      } finally {
        setMessagesLoading(false);
      }
    },
    [ticketRouteKey]
  );

  const loadActivities = useCallback(async () => {
    if (!ticketRouteKey) return;
    setActivitiesLoading(true);
    try {
      const response: any = await getTicketActivity(ticketRouteKey, "", {
        per_page: 50,
      });
      const data: ActivityItem[] = Array.isArray(response?.data) ? response.data : [];
      setActivities(data);
    } finally {
      setActivitiesLoading(false);
    }
  }, [ticketRouteKey]);

  const loadRelations = useCallback(async () => {
    if (!ticketRouteKey) return;
    try {
      const response: any = await getTicketRelations(ticketRouteKey);
      setRelations(Array.isArray(response?.data) ? response.data : []);
    } catch {
      // ignore
    }
  }, [ticketRouteKey]);

  const handleAddRelation = useCallback(async () => {
    if (!ticket || !relationTicketId || !canManageRelations) return;
    setAddingRelation(true);
    try {
      await postTicketRelation(ticket.uuid || ticket.id, {
        related_ticket_id: Number(relationTicketId),
        relation_type: relationType,
      });
      toast.success("Relation added");
      setShowAddRelationModal(false);
      setRelationTicketId("");
      void loadRelations();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to add relation");
    } finally {
      setAddingRelation(false);
    }
  }, [canManageRelations, loadRelations, relationTicketId, relationType, ticket]);

  const handleRemoveRelation = useCallback(async (relationId: number) => {
    if (!ticket || !canManageRelations) return;
    try {
      await deleteTicketRelation(ticket.uuid || ticket.id, relationId);
      setRelations((prev) => prev.filter((relation) => relation.id !== relationId));
      toast.success("Relation removed");
    } catch {
      toast.error("Failed to remove relation");
    }
  }, [canManageRelations, ticket]);

  useEffect(() => {
    if (!canView) {
      navigate("/no-access");
      return;
    }
    if (!ticketRouteKey) {
      navigate("/tickets");
      return;
    }
    void loadTicket();
    void loadMessages(1);
    void loadRelations();
  }, [canView, loadMessages, loadRelations, loadTicket, navigate, ticketRouteKey]);

  useEffect(() => {
    return ticketBus.on("ticket-message-created", (payload: TicketMessageRealtimePayload) => {
      if (!payload || payload.ticket_uuid !== (ticket?.uuid || ticketRouteKey)) {
        return;
      }

      const incomingMessage = payload.message;
      if (!incomingMessage?.id) {
        return;
      }

      shouldAutoScrollRef.current = true;
      setMessages((prev) => {
        if (prev.some((item) => item.id === incomingMessage.id)) {
          return prev;
        }

        return [...prev, incomingMessage].sort((a, b) => a.id - b.id);
      });

      if (payload.ticket) {
        setTicket(payload.ticket);
        hydrateTicketForm(payload.ticket);
      }

      if (ticketRouteKey) {
        void postTicketMessagesRead(ticketRouteKey).catch(() => undefined);
      }
    });
  }, [ticket?.uuid, ticketRouteKey, hydrateTicketForm]);

  useEffect(() => {
    const handleTicketRealtime = (payload: TicketRealtimePayload) => {
      if (!payload || payload.ticket_uuid !== (ticket?.uuid || ticketRouteKey)) {
        return;
      }

      if (payload.ticket) {
        setTicket(payload.ticket);
        hydrateTicketForm(payload.ticket);
      } else {
        void loadTicket();
      }
    };

    const unsubscribeUpdated = ticketBus.on("ticket-updated", handleTicketRealtime);
    const unsubscribeAccepted = ticketBus.on("ticket-accepted", handleTicketRealtime);
    const unsubscribeDeleted = ticketBus.on("ticket-deleted", (payload) => {
      if (!payload || payload.ticket_uuid !== (ticket?.uuid || ticketRouteKey)) {
        return;
      }

      toast.warning("This ticket has been deleted.");
      navigate("/tickets");
    });

    return () => {
      unsubscribeUpdated();
      unsubscribeAccepted();
      unsubscribeDeleted();
    };
  }, [ticket?.uuid, ticketRouteKey, hydrateTicketForm, loadTicket, navigate]);

  useEffect(() => {
    return ticketBus.on("ws-reconnected", () => {
      void loadTicket();
      void loadMessages(1);
    });
  }, [loadTicket, loadMessages]);

  useEffect(() => {
    if (!ticketRouteKey || !canView) {
      return;
    }

    const toUser = (raw: any): TicketUser => ({
      id: Number(raw?.id || 0),
      name: String(raw?.name || "User"),
    });

    const upsertOnline = (incoming: TicketUser) => {
      if (!incoming.id) return;
      setOnlineUsers((prev) => {
        if (prev.some((item) => item.id === incoming.id)) {
          return prev;
        }
        return [...prev, incoming];
      });
    };

    const channel = MyEcho.join(`ticket.${ticketRouteKey}`)
      .here((users: any[]) => {
        const normalized = (Array.isArray(users) ? users : [])
          .map(toUser)
          .filter((item) => item.id > 0);
        setOnlineUsers(normalized);
      })
      .joining((user: any) => {
        upsertOnline(toUser(user));
      })
      .leaving((user: any) => {
        const leavingId = Number(user?.id || 0);
        if (!leavingId) return;
        setOnlineUsers((prev) => prev.filter((item) => item.id !== leavingId));
      })
      .listenForWhisper("typing", (data: any) => {
        const typingUserId = Number(data?.userId || 0);
        const typingName = String(data?.name || "");
        if (!typingUserId || typingUserId === currentUserId || !typingName) {
          return;
        }

        setTypingUser(typingName);
        if (typingHideTimeoutRef.current) {
          clearTimeout(typingHideTimeoutRef.current);
        }
        typingHideTimeoutRef.current = setTimeout(() => {
          setTypingUser(null);
        }, 2000);
      });

    presenceChannelRef.current = channel;

    return () => {
      presenceChannelRef.current = null;
      if (typingHideTimeoutRef.current) {
        clearTimeout(typingHideTimeoutRef.current);
        typingHideTimeoutRef.current = null;
      }
      setTypingUser(null);
      setOnlineUsers([]);
      MyEcho.leave(`ticket.${ticketRouteKey}`);
    };
  }, [canView, currentUserId, ticketRouteKey]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) {
      return;
    }
    shouldAutoScrollRef.current = false;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const loadCannedResponses = useCallback(async () => {
    try {
      const response: any = await getTicketCannedResponses({
        category_id: ticket?.category_id || undefined,
      });
      setCannedResponses(Array.isArray(response?.data) ? response.data : []);
    } catch {
      setCannedResponses([]);
    }
  }, [ticket?.category_id]);

  const resetCannedResponseForm = useCallback(() => {
    setEditingCannedResponse(null);
    setCannedResponseTitle("");
    setCannedResponseBody("");
    setCannedResponseScope(ticket?.category_id ? "category" : "all");
    setCannedResponseShared(false);
  }, [ticket?.category_id]);

  const closeCannedResponseModal = useCallback(() => {
    if (savingCannedResponse) {
      return;
    }

    setShowCannedResponseModal(false);
    resetCannedResponseForm();
  }, [resetCannedResponseForm, savingCannedResponse]);

  const openCreateCannedResponseModal = useCallback(() => {
    resetCannedResponseForm();
    setShowCannedResponseModal(true);
  }, [resetCannedResponseForm]);

  const openEditCannedResponseModal = useCallback((response: CannedResponse) => {
    setEditingCannedResponse(response);
    setCannedResponseTitle(response.title || "");
    setCannedResponseBody(response.body || "");
    setCannedResponseScope(response.category_id ? "category" : "all");
    setCannedResponseShared(Boolean(response.is_shared));
    setShowCannedResponseModal(true);
  }, []);

  const handleSaveCannedResponse = useCallback(async () => {
    const trimmedTitle = cannedResponseTitle.trim();
    const trimmedBody = cannedResponseBody.trim();

    if (!trimmedTitle || !trimmedBody) {
      toast.error("Template title and message are required.");
      return;
    }

    setSavingCannedResponse(true);

    try {
      const payload = {
        title: trimmedTitle,
        body: trimmedBody,
        category_id: cannedResponseScope === "category" ? (ticket?.category_id ?? null) : null,
        is_shared: canManageSharedCannedResponses ? cannedResponseShared : false,
      };

      if (editingCannedResponse?.id) {
        await updateTicketCannedResponse(editingCannedResponse.id, payload);
        toast.success("Template updated");
      } else {
        await postTicketCannedResponse(payload);
        toast.success("Template created");
      }

      await loadCannedResponses();
      setShowCannedResponseModal(false);
      resetCannedResponseForm();
    } catch (error: any) {
      toast.error(getApiErrorMessage(
        error,
        editingCannedResponse ? "Failed to update template." : "Failed to create template."
      ));
    } finally {
      setSavingCannedResponse(false);
    }
  }, [
    canManageSharedCannedResponses,
    cannedResponseBody,
    cannedResponseScope,
    cannedResponseShared,
    cannedResponseTitle,
    editingCannedResponse,
    loadCannedResponses,
    resetCannedResponseForm,
    ticket?.category_id,
  ]);

  const handleDeleteCannedResponse = useCallback(async (response: CannedResponse) => {
    if (!canDeleteCannedResponse(response)) {
      toast.error("You do not have permission to delete this template.");
      return;
    }

    setDeletingCannedResponse(response);
  }, [canDeleteCannedResponse]);

  const closeDeleteCannedResponseModal = useCallback(() => {
    if (removingCannedResponse) {
      return;
    }

    setDeletingCannedResponse(null);
  }, [removingCannedResponse]);

  const confirmDeleteCannedResponse = useCallback(async () => {
    if (!deletingCannedResponse) {
      return;
    }

    if (!canDeleteCannedResponse(deletingCannedResponse)) {
      toast.error("You do not have permission to delete this template.");
      setDeletingCannedResponse(null);
      return;
    }

    setRemovingCannedResponse(true);

    try {
      await deleteTicketCannedResponse(deletingCannedResponse.id);
      setCannedResponses((prev) => prev.filter((item) => item.id !== deletingCannedResponse.id));
      setDeletingCannedResponse(null);
      toast.success("Template deleted");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to delete template."));
    } finally {
      setRemovingCannedResponse(false);
    }
  }, [canDeleteCannedResponse, deletingCannedResponse]);

  const openRatingModal = useCallback(() => {
    setRatingValue(ticket?.satisfaction_rating?.rating || 0);
    setRatingComment(ticket?.satisfaction_rating?.comment || "");
    setRatingHover(0);
    setShowRatingModal(true);
  }, [ticket?.satisfaction_rating?.comment, ticket?.satisfaction_rating?.rating]);

  const closeRatingModal = useCallback(() => {
    if (submittingRating) {
      return;
    }

    setShowRatingModal(false);
    setRatingHover(0);
  }, [submittingRating]);

  const submitRating = useCallback(async () => {
    if (!ticket || !ratingValue) return;
    setSubmittingRating(true);
    try {
      await postTicketRating(ticket.uuid || ticket.id, {
        rating: ratingValue,
        comment: ratingComment.trim() || undefined,
      });
      toast.success("Rating submitted");
      setTicket((prev) => (
        prev
          ? {
              ...prev,
              satisfaction_rating: {
                id: 0,
                rating: ratingValue,
                comment: ratingComment.trim() || null,
              },
            }
          : prev
      ));
      setShowRatingModal(false);
      setRatingHover(0);
    } catch {
      toast.error("Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  }, [ratingComment, ratingValue, ticket]);

  const sendTypingWhisper = useCallback(() => {
    const now = Date.now();
    if (now - typingWhisperCooldownRef.current < 700) {
      return;
    }

    const channel = presenceChannelRef.current;
    if (!channel || !currentUserId) {
      return;
    }

    typingWhisperCooldownRef.current = now;
    channel.whisper("typing", {
      userId: currentUserId,
      name: currentUserName,
    });
  }, [currentUserId, currentUserName]);

  const handleAccept = useCallback(async () => {
    if (!ticket || !canAccept) return;
    try {
      const response: any = await acceptTicket(ticket.uuid || ticket.id);
      const nextTicket: TicketData | null = response?.data ?? null;
      if (nextTicket) {
        setTicket(nextTicket);
        hydrateTicketForm(nextTicket);
      } else {
        await loadTicket();
      }
      ticketBus.emit("ticket-count-refresh");
      toast.success("Ticket accepted");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to accept ticket."));
    }
  }, [canAccept, hydrateTicketForm, loadTicket, ticket]);

  const openReassignModal = useCallback(async () => {
    if (!ticket || !canAssign) return;
    setReassignUserId("");
    setShowReassignModal(true);
    try {
      const response: any = await getUsersAll();
      const users = normalizeUserOptions(response);
      setAssignableUsers(users.filter((user) => user.id !== ticket.created_by_user_id));
    } catch {
      setAssignableUsers([]);
    }
  }, [canAssign, ticket]);

  const handleReassign = useCallback(async () => {
    if (!ticket || !reassignUserId) return;
    setReassigningTicket(true);
    try {
      const response: any = await postTicketAssign(ticket.uuid || ticket.id, Number(reassignUserId));
      const nextTicket: TicketData | null = response?.data ?? null;
      if (nextTicket) {
        setTicket(nextTicket);
        hydrateTicketForm(nextTicket);
      } else {
        await loadTicket();
      }
      toast.success("Ticket reassigned");
      setShowReassignModal(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to reassign ticket");
    } finally {
      setReassigningTicket(false);
    }
  }, [hydrateTicketForm, loadTicket, reassignUserId, ticket]);

  const handleToggleWatch = useCallback(async () => {
    if (!ticket) return;
    setWatchLoading(true);
    try {
      if (isWatching) {
        await deleteTicketWatch(ticket.uuid || ticket.id);
        setIsWatching(false);
        toast.info("Stopped watching ticket");
      } else {
        await postTicketWatch(ticket.uuid || ticket.id);
        setIsWatching(true);
        toast.info("Now watching ticket");
      }
    } catch {
      toast.error("Failed to update watch status");
    } finally {
      setWatchLoading(false);
    }
  }, [isWatching, ticket]);

  const handleTicketSave = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ticket || !canUpdateTicket) return;
    setSavingTicket(true);
    try {
      const updatePayload = canUpdateOnlyStatus
        ? { status: editStatus }
        : {
            subject: editSubject,
            description: editDescription,
            status: editStatus,
            priority: editPriority,
          };

      const response: any = await updateTicket(updatePayload, ticket.uuid || ticket.id);
      const nextTicket: TicketData | null = response?.data ?? null;
      if (nextTicket) {
        setTicket(nextTicket);
        hydrateTicketForm(nextTicket);
      } else {
        await loadTicket();
      }
      ticketBus.emit("ticket-count-refresh");
      toast.success(canUpdateOnlyStatus ? "Ticket status updated" : "Ticket updated");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to update ticket."));
    } finally {
      setSavingTicket(false);
    }
  }, [
    canUpdateTicket,
    canUpdateOnlyStatus,
    editDescription,
    editPriority,
    editStatus,
    editSubject,
    hydrateTicketForm,
    loadTicket,
    ticket,
  ]);

  const openDeleteModal = useCallback(() => {
    if (!ticket || !canDelete) return;
    setDeleteConfirmation("");
    setDeleteModalOpen(true);
  }, [canDelete, ticket]);

  const closeDeleteModal = useCallback(() => {
    if (deletingTicket) return;
    setDeleteModalOpen(false);
    setDeleteConfirmation("");
  }, [deletingTicket]);

  const handleDelete = useCallback(async () => {
    if (!ticket || !canDelete) return;

    const expectedConfirmation = `delete ${ticket.id}`;
    if (deleteConfirmation.trim() !== expectedConfirmation) {
      toast.error(`Confirmation phrase must be exactly "${expectedConfirmation}".`);
      return;
    }

    setDeletingTicket(true);
    try {
      await deleteTicket(ticket.uuid || ticket.id, expectedConfirmation);
      ticketBus.emit("ticket-count-refresh");
      toast.success("Ticket deleted");
      setDeleteModalOpen(false);
      setDeleteConfirmation("");
      navigate("/tickets");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to delete ticket."));
    } finally {
      setDeletingTicket(false);
    }
  }, [canDelete, deleteConfirmation, navigate, ticket]);

  const handleSendMessage = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!ticket || !canSendMessage) return;
    if (!messageBody.trim() && messageFiles.length === 0) {
      return;
    }

    setSendingMessage(true);
    try {
      const formData = new FormData();
      if (messageBody.trim()) {
        formData.append("body", messageBody.trim());
      }
      if (isInternalNote) {
        formData.append("is_internal", "1");
      }
      messageFiles.forEach((file) => {
        formData.append("attachments[]", file);
      });

      const response: any = await postTicketMessage(ticket.uuid || ticket.id, formData);
      const createdMessage: TicketMessage | null = response?.data ?? null;
      if (createdMessage) {
        shouldAutoScrollRef.current = true;
        setMessages((prev) => [...prev, createdMessage]);
      } else {
        await loadMessages(1);
      }

      setMessageBody("");
      setMessageFiles([]);
      setMessageFileInputKey((prev) => prev + 1);
      setIsInternalNote(false);
      toast.success(isInternalNote ? "Internal note added" : "Message sent");
    } catch (error: any) {
      toast.error(getApiErrorMessage(error, "Failed to send message."));
    } finally {
      setSendingMessage(false);
    }
  }, [canSendMessage, isInternalNote, loadMessages, messageBody, messageFiles, ticket]);

  const handleRemoveMessageFile = useCallback((fileIndex: number) => {
    setMessageFiles((prev) => prev.filter((_, index) => index !== fileIndex));
    setMessageFileInputKey((prev) => prev + 1);
  }, []);

  const handleMessageInputKeyDown = useCallback((event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.altKey) {
      return;
    }

    const shouldSubmit = isMacKeyboardPlatform ? event.metaKey : event.ctrlKey;
    if (!shouldSubmit) {
      return;
    }

    event.preventDefault();
    if (sendingMessage) {
      return;
    }

    event.currentTarget.form?.requestSubmit();
  }, [isMacKeyboardPlatform, sendingMessage]);

  const handleMessageBodyChange = useCallback((value: string) => {
    setMessageBody(value);
    if (value.trim()) {
      sendTypingWhisper();
    }
  }, [sendTypingWhisper]);

  const handleToggleCannedResponses = useCallback(() => {
    const nextValue = !showCannedResponses;
    if (nextValue) {
      void loadCannedResponses();
    }
    setShowCannedResponses(nextValue);
  }, [loadCannedResponses, showCannedResponses]);

  const handleSelectCannedResponse = useCallback((responseTemplate: CannedResponse) => {
    setMessageBody(responseTemplate.body);
    setShowCannedResponses(false);
    void postUseTicketCannedResponse(responseTemplate.id).catch(() => undefined);
  }, []);

  const handleShowConversation = useCallback(() => {
    setActiveTab("conversation");
  }, []);

  const handleShowActivity = useCallback(() => {
    setActiveTab("activity");
    if (activities.length === 0) {
      void loadActivities();
    }
  }, [activities.length, loadActivities]);

  const handleDownloadAttachmentClick = useCallback((attachment: TicketAttachment) => {
    void handleDownloadAttachment(attachment);
  }, [handleDownloadAttachment]);

  const handleOpenImagePreview = useCallback((attachment: TicketAttachment) => {
    void openImagePreview(attachment);
  }, [openImagePreview]);

  const loadOlderAvailable = useMemo(
    () => messagesPagination.current_page < messagesPagination.total_page,
    [messagesPagination.current_page, messagesPagination.total_page]
  );
  const groupedMessages = useMemo<MessageGroup[]>(() => {
    return messages.reduce<MessageGroup[]>((groups, message) => {
      const nextKey = getConversationDateKey(message.created_at);
      const lastGroup = groups[groups.length - 1];

      if (!lastGroup || lastGroup.key !== nextKey) {
        groups.push({
          key: nextKey,
          label: getConversationDateLabel(message.created_at),
          messages: [message],
        });
        return groups;
      }

      lastGroup.messages.push(message);
      return groups;
    }, []);
  }, [messages]);

  if (!canView) {
    return null;
  }

  if (loading && !ticket) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex justify-content-center py-5">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-content">
        <Container fluid>
          <Alert color={ticketNotFound ? "danger" : "warning"} fade={false}>
            {ticketNotFound ? "Ticket not found." : ticketLoadError || "Failed to load ticket."}
          </Alert>
          {!ticketNotFound && (
            <Button color="primary" className="me-2" onClick={() => void loadTicket()}>
              Retry
            </Button>
          )}
          <Link to="/tickets" className="btn btn-primary">
            Back to tickets
          </Link>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Support" breadcrumbItem={`Ticket #${ticket.id}`} />

          {ticketLoadError && (
            <Alert color="warning" fade={false} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>{ticketLoadError}. Showing last loaded ticket data.</span>
              <Button color="warning" outline size="sm" onClick={() => void loadTicket()}>
                Retry
              </Button>
            </Alert>
          )}

          {messagesLoadError && (
            <Alert color="warning" fade={false} className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>{messagesLoadError}</span>
              <Button color="warning" outline size="sm" onClick={() => void loadMessages(messagesPagination.current_page || 1)}>
                Retry
              </Button>
            </Alert>
          )}

          <Row className="g-3 mb-3">
            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <Link to="/tickets" className="btn btn-light">
                Back to list
              </Link>
              {canAccept && !ticket.assignee && (
                <Button color="info" onClick={() => void handleAccept()}>
                  Accept ticket
                </Button>
              )}
              <Button
                color={isWatching ? "warning" : "light"}
                outline={!isWatching}
                onClick={() => void handleToggleWatch()}
                disabled={watchLoading}
              >
                {watchLoading ? "Updating..." : isWatching ? "Watching" : "Watch"}
              </Button>
              {canAssign && ticket.assignee && (
                <Button color="primary" outline onClick={() => void openReassignModal()}>
                  Reassign
                </Button>
              )}
              {canRateTicket && !ticket.satisfaction_rating && (
                <Button color="warning" onClick={openRatingModal}>
                  Leave feedback
                </Button>
              )}
              {canDelete && (
                <Button color="danger" onClick={openDeleteModal} disabled={deletingTicket}>
                  {deletingTicket ? "Deleting..." : "Delete"}
                </Button>
              )}
            </div>
          </Row>

          <Row className="g-3">
            <Col xl={4}>
              <TicketDetailsSidebar
                ticket={ticket}
                canUpdateTicket={canUpdateTicket}
                canManageRelations={canManageRelations}
                canUpdateOnlyStatus={canUpdateOnlyStatus}
                canViewFeedback={canViewFeedback}
                canRateTicket={canRateTicket}
                savingTicket={savingTicket}
                editSubject={editSubject}
                editDescription={editDescription}
                editStatus={editStatus}
                editPriority={editPriority}
                availableStatusOptions={availableStatusOptions}
                relations={relations}
                onEditSubjectChange={setEditSubject}
                onEditDescriptionChange={setEditDescription}
                onEditStatusChange={setEditStatus}
                onEditPriorityChange={setEditPriority}
                onOpenAddRelationModal={() => setShowAddRelationModal(true)}
                onRemoveRelation={(relationId) => {
                  void handleRemoveRelation(relationId);
                }}
                onOpenRatingModal={openRatingModal}
                onSubmit={handleTicketSave}
              />
            </Col>

            <Col xl={8}>
              <TicketConversationPanel
                activeTab={activeTab}
                groupedMessages={groupedMessages}
                messages={messages}
                messagesLoading={messagesLoading}
                messagesEndRef={messagesEndRef}
                conversationWrapRef={conversationWrapRef}
                attachmentPreviewUrls={attachmentPreviewUrls}
                previewFailedIds={previewFailedIds}
                currentUserId={currentUserId}
                typingUser={typingUser}
                onlineUsersCount={onlineUsers.length}
                loadOlderAvailable={loadOlderAvailable}
                canSendMessage={canSendMessage}
                isConversationLocked={isConversationLocked}
                canViewInternalNotes={canViewInternalNotes}
                canUseCannedResponses={canUseCannedResponses}
                canCreateCannedResponses={canCreateCannedResponses}
                showCannedResponses={showCannedResponses}
                cannedResponses={cannedResponses}
                currentCannedResponseCategoryLabel={cannedResponseCategoryLabel}
                messageBody={messageBody}
                messageFileInputKey={messageFileInputKey}
                messageFiles={messageFiles}
                isInternalNote={isInternalNote}
                messageComposerDisabled={messageComposerDisabled}
                sendingMessage={sendingMessage}
                sendMessageShortcutLabel={sendMessageShortcutLabel}
                activitiesLoading={activitiesLoading}
                activities={activities}
                onShowConversation={handleShowConversation}
                onShowActivity={handleShowActivity}
                onLoadOlder={() => {
                  void loadMessages(messagesPagination.current_page + 1, true);
                }}
                onDownloadAttachment={handleDownloadAttachmentClick}
                onOpenImagePreview={handleOpenImagePreview}
                onEnsurePreview={ensureAttachmentPreviewLoaded}
                onToggleCannedResponses={handleToggleCannedResponses}
                onSelectCannedResponse={handleSelectCannedResponse}
                onOpenCreateCannedResponse={openCreateCannedResponseModal}
                onOpenEditCannedResponse={openEditCannedResponseModal}
                onDeleteCannedResponse={(response) => {
                  void handleDeleteCannedResponse(response);
                }}
                canEditCannedResponse={canEditCannedResponse}
                canDeleteCannedResponse={canDeleteCannedResponse}
                onMessageBodyChange={handleMessageBodyChange}
                onMessageInputKeyDown={handleMessageInputKeyDown}
                onMessageFilesChange={setMessageFiles}
                onRemoveMessageFile={handleRemoveMessageFile}
                onToggleInternalNote={() => setIsInternalNote((prev) => !prev)}
                onSubmitMessage={handleSendMessage}
              />
            </Col>
          </Row>
        </Container>
      </div>

      <TicketRatingModal
        isOpen={showRatingModal}
        ticketId={ticket.id}
        ratingValue={ratingValue}
        ratingHover={ratingHover}
        ratingPreviewValue={ratingPreviewValue}
        ratingComment={ratingComment}
        submittingRating={submittingRating}
        onToggle={closeRatingModal}
        onChangeRating={setRatingValue}
        onChangeHover={setRatingHover}
        onChangeComment={setRatingComment}
        onSubmit={() => void submitRating()}
      />

      <TicketAddRelationModal
        isOpen={showAddRelationModal}
        relationTicketId={relationTicketId}
        relationType={relationType}
        addingRelation={addingRelation}
        onToggle={() => setShowAddRelationModal(false)}
        onChangeRelationTicketId={setRelationTicketId}
        onChangeRelationType={setRelationType}
        onSubmit={() => void handleAddRelation()}
      />

      <TicketCannedResponseModal
        isOpen={showCannedResponseModal}
        isEditing={Boolean(editingCannedResponse)}
        titleValue={cannedResponseTitle}
        bodyValue={cannedResponseBody}
        scopeValue={cannedResponseScope}
        canUseCurrentCategory={Boolean(ticket?.category_id)}
        currentCategoryLabel={cannedResponseCategoryLabel}
        isShared={cannedResponseShared}
        canManageSharedResponses={canManageSharedCannedResponses}
        savingResponse={savingCannedResponse}
        onToggle={closeCannedResponseModal}
        onChangeTitle={setCannedResponseTitle}
        onChangeBody={setCannedResponseBody}
        onChangeScope={setCannedResponseScope}
        onChangeShared={setCannedResponseShared}
        onSubmit={() => void handleSaveCannedResponse()}
      />

      <TicketCannedResponseDeleteModal
        isOpen={Boolean(deletingCannedResponse)}
        responseTitle={deletingCannedResponse?.title || ""}
        deletingResponse={removingCannedResponse}
        onToggle={closeDeleteCannedResponseModal}
        onSubmit={() => void confirmDeleteCannedResponse()}
      />

      <TicketReassignModal
        isOpen={showReassignModal}
        ticket={ticket}
        reassignUserId={reassignUserId}
        reassigningTicket={reassigningTicket}
        assignableUsers={assignableUsers}
        onToggle={() => setShowReassignModal(false)}
        onChangeUserId={setReassignUserId}
        onSubmit={() => void handleReassign()}
      />

      <TicketDeleteModal
        isOpen={deleteModalOpen}
        ticket={ticket}
        deleteExpectedPhrase={deleteExpectedPhrase}
        deleteConfirmation={deleteConfirmation}
        deletingTicket={deletingTicket}
        canConfirmDelete={canConfirmDelete}
        onToggle={closeDeleteModal}
        onChangeConfirmation={setDeleteConfirmation}
        onSubmit={() => void handleDelete()}
      />
    </React.Fragment>
  );
};

export default TicketDetailsPage;
